// src/components/Purchase-orders/POList.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { POContext } from "../../contexts/POContext";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useMaterials } from "../../contexts/MaterialContext";
import POCreateModal from "./POCreateModal";

const LS_DRAFT = "mudoDraftPO";

const j = {
  get: (k, f = null) => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch { return f; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
  del: (k) => { try { localStorage.removeItem(k); } catch {} },
};

const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const getStock = (m) => toNum(m?.stock ?? m?.stockQty ?? m?.startQuantity ?? 0);
const getLowLimit = (m) => toNum(m?.lowStockLimit ?? m?.minStock ?? m?.reorderPoint ?? m?.reorderLevel ?? 0);
const getUnitCost = (m) => {
  const purchaseCost = toNum(m?.purchaseCost);
  const conv = Math.max(1, toNum(m?.conversionAmount) || 1);
  return conv > 0 ? purchaseCost / conv : 0;
};
const getUnit = (m) => m?.conversionUnit || m?.trackingUnit || m?.unit || "unit";

const getSupplierFrom = (m, it) =>
  (it?.supplier ??
    m?.supplier ??
    m?.supplierName ??
    m?.vendor ??
    m?.brand ??
    m?.maker ??
    m?.manufacturer ??
    "") || "";

const normalizeUrl = (u) => {
  if (!u) return "";
  let s = String(u).trim();
  if (!s) return "";
  if (typeof u === "object" && u.url) s = String(u.url).trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("www.")) return `https://${s}`;
  if (/^[a-z0-9.-]+\.[a-z]{2,}([/:].*)?$/i.test(s)) return `https://${s}`;
  return s;
};
const getWebsiteFrom = (m, it) => {
  const candidates = [
    it?.website, it?.supplierLink, it?.url, it?.href, it?.link,
    m?.supplierLink, m?.website, m?.Website, m?.webSite, m?.productUrl, m?.productURL,
    m?.supplierUrl, m?.supplierURL, m?.supplierWebsite, m?.websiteUrl, m?.websiteURL,
    m?.url, m?.href, m?.link, m?.web, m?.page, m?.homepage, m?.buyUrl, m?.shopUrl,
    m?.storeUrl, m?.sourceUrl, m?.vendorUrl, m?.manufacturerUrl,
    m?.supplier?.url, m?.supplier?.website, m?.supplier?.webSite,
    m?.vendor?.url, m?.vendor?.website,
    m?.manufacturer?.url, m?.manufacturer?.website,
    m?.source?.url,
  ];
  const first = candidates.find((v) => (typeof v === "string" && v.trim()) || (v && typeof v === "object" && v.url));
  return normalizeUrl(first || "");
};

export default function POList() {
  const navigate = useNavigate();
  const { play, buzz } = useFeedback();

  const poCtx = useContext(POContext) || {};
  const { orders: ctxOrders = [], updateOrder, removeOrder } = poCtx;

  const { materials = [], scrapedMaterials = [], bulkReceive } = useMaterials();
  const allMaterials = (scrapedMaterials?.length ? scrapedMaterials : materials) || [];

  const matById = useMemo(() => {
    const map = new Map();
    allMaterials.forEach((m) => map.set(String(m.id), m));
    return map;
  }, [allMaterials]);
  const matBySku = useMemo(() => {
    const map = new Map();
    allMaterials.forEach((m) => {
      const k = (m?.sku ?? "").toString().trim().toLowerCase();
      if (k) map.set(k, m);
    });
    return map;
  }, [allMaterials]);
  const matByName = useMemo(() => {
    const map = new Map();
    allMaterials.forEach((m) => {
      const k = (m?.name ?? "").toString().trim().toLowerCase();
      if (k) map.set(k, m);
    });
    return map;
  }, [allMaterials]);

  const [orders, setOrders] = useState(ctxOrders);
  useEffect(() => { setOrders(Array.isArray(ctxOrders) ? ctxOrders : []); }, [ctxOrders]);

  // Draft handoff (from Receipts or widget)
  const [showCreate, setShowCreate] = useState(false);
  const [draftItems, setDraftItems] = useState([]);

  useEffect(() => {
    const loadDraft = () => {
      const d = j.get(LS_DRAFT, null);
      if (!d?.items?.length) return;
      const resolved = d.items.map((it) => {
        let m = it.materialId ? matById.get(String(it.materialId)) : null;
        if (!m && it.sku) m = matBySku.get(String(it.sku).toLowerCase());
        if (!m && it.name) m = matByName.get(String(it.name).toLowerCase());

        const unit = it.unit || (m ? getUnit(m) : "unit");
        const unitCost = Number(it.unitCost ?? (m ? getUnitCost(m) : 0)) || 0;

        return {
          id: String(it.materialId || it.id || m?.id || Date.now()),
          materialId: it.materialId || m?.id || undefined,
          name: it.name || m?.name || "Unnamed",
          sku: it.sku ?? m?.sku ?? "",
          supplier: getSupplierFrom(m, it),
          website: getWebsiteFrom(m, it),
          category: it.category ?? m?.category ?? "",
          unit,
          unitCost,
          stock: m ? getStock(m) : undefined,
          min: m ? getLowLimit(m) : undefined,
          image: it.image ?? m?.image ?? m?.photo ?? "",
          suggestedQty: Number(it.qty || 0) || 1,
        };
      });

      setDraftItems(resolved);
      setShowCreate(true);
    };

    loadDraft();
    const onStorage = (e) => { if (e.key === LS_DRAFT) loadDraft(); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [matById, matBySku, matByName]);

  // Reorder suggestions
  const suggestions = useMemo(() => {
    return (allMaterials || [])
      .map((m) => {
        const stock = getStock(m);
        const min = getLowLimit(m);
        const unit = getUnit(m);
        const cost = getUnitCost(m);
        const needsReorder = min > 0 ? stock <= min : stock <= 0;
        if (!needsReorder) return null;

        const toLevel = toNum(m?.reorderTo);
        const defaultOrderQty = toNum(m?.defaultOrderQty) || Math.max(1, toNum(m?.conversionAmount)) || 1;

        let suggestedQty = 0;
        if (toLevel > 0) suggestedQty = Math.max(0, toLevel - stock);
        else if (min > 0) suggestedQty = Math.max(min * 2 - stock, min);
        else suggestedQty = defaultOrderQty;
        suggestedQty = Math.max(0, Math.round(suggestedQty));

        return {
          id: m.id,
          name: m.name || "Unnamed",
          sku: m.sku || "",
          supplier: getSupplierFrom(m, null),
          website: getWebsiteFrom(m, null),
          category: m.category || "",
          unit,
          unitCost: cost,
          stock,
          min,
          suggestedQty,
          image: m.image || m.photo || "",
        };
      })
      .filter(Boolean)
      .filter((s) => s.suggestedQty > 0);
  }, [allMaterials]);

  const [qtyOverrides, setQtyOverrides] = useState({});
  const qtyFor = (id, fallback) => Number(qtyOverrides[id] ?? fallback ?? 0);

  const { play: pPlay = () => {}, buzz: pBuzz = () => {} } = { play, buzz };

  const createDraftFromSuggestions = () => {
    if (!suggestions.length) return;
    const items = suggestions.map((s) => ({
      materialId: s.id,
      name: s.name,
      sku: s.sku,
      supplier: s.supplier,
      website: s.website,
      category: s.category,
      unit: s.unit,
      unitCost: Number(s.unitCost || 0),
      qty: qtyFor(s.id, s.suggestedQty),
      image: s.image,
    }));
    const d = { id: `draft-${Date.now()}`, createdAt: Date.now(), status: "Draft", items };
    j.set(LS_DRAFT, d);
    setDraftItems(items);
    setShowCreate(true);
    pPlay("success"); pBuzz("success");
  };

  // Filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [supplierFilter, setSupplierFilter] = useState("All");

  const suppliers = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => (o.items || []).forEach((it) => it.supplier && set.add(it.supplier)));
    return ["All", ...Array.from(set).sort()];
  }, [orders]);

  const currency = (n) => (typeof n === "number" && isFinite(n) ? `$${n.toFixed(2)}` : "—");
  const orderTotal = (o) => (o?.items || []).reduce((sum, it) => sum + Number(it.unitCost || 0) * Number(it.qty || 0), 0);

  // Edit/receive/delete
  const [editOrderId, setEditOrderId] = useState(null);
  const [editLines, setEditLines] = useState({});

  const beginEdit = (order) => {
    setEditOrderId(order.id);
    const map = {};
    (order.items || []).forEach((it) => (map[it.id || it.materialId] = Number(it.qty || 0)));
    setEditLines(map);
    pPlay("tap"); pBuzz("light");
  };

  const saveEdit = (order) => {
    const updated = {
      ...order,
      items: (order.items || []).map((it) => ({
        ...it,
        qty: Number(editLines[it.id || it.materialId] ?? it.qty ?? 0),
      })),
    };
    if (typeof updateOrder === "function") updateOrder(updated);
    setEditOrderId(null);
    setEditLines({});
    pPlay("success"); pBuzz("success");
  };

  const markReceived = (order) => {
    try {
      const lines = (order.items || [])
        .map((it) => ({ materialId: it.materialId || it.id, qty: Number(it.qty || 0) }))
        .filter((l) => l.materialId && l.qty > 0);
      if (lines.length) bulkReceive(lines);
    } catch {}
    const updated = { ...order, status: "Received", receivedAt: Date.now() };
    if (typeof updateOrder === "function") updateOrder(updated);
    pPlay("success"); pBuzz("success");
  };

  const deleteOrder = (orderId) => {
    pPlay("tap");
    const ok = window.confirm("Delete this purchase order? This cannot be undone.");
    if (!ok) { pBuzz("light"); return; }
    if (typeof removeOrder === "function") removeOrder(orderId);
    pPlay("stop"); pBuzz("medium");
  };

  // Enrich orders for display
  const enrichedOrders = useMemo(() => {
    return (orders || []).map((o) => ({
      ...o,
      createdAt: o.createdAt ?? Date.now(),
      items: (o.items || []).map((it) => {
        let m = it.materialId ? matById.get(String(it.materialId)) : null;
        if (!m && it.sku) m = matBySku.get(String(it.sku).toLowerCase());
        if (!m && it.name) m = matByName.get(String(it.name).toLowerCase());

        const name = it.name || m?.name || "Unnamed";
        const sku = it.sku ?? m?.sku ?? "";
        const supplier = getSupplierFrom(m, it);
        const website = normalizeUrl(it.website) || getWebsiteFrom(m, it);
        const unit = it.unit || (m ? getUnit(m) : "unit");
        const unitCost = Number(it.unitCost ?? (m ? getUnitCost(m) : 0)) || 0;
        const image = it.image ?? m?.image ?? m?.photo ?? "";

        return { ...it, name, sku, supplier, website, unit, unitCost, image, materialId: it.materialId || m?.id };
      }),
    }));
  }, [orders, matById, matBySku, matByName]);

  const filteredOrders = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return enrichedOrders.filter((o) => {
      if (statusFilter !== "All" && (o.status || "Open") !== statusFilter) return false;
      if (supplierFilter !== "All") {
        const hasSupplier = (o.items || []).some((it) => (it.supplier || "") === supplierFilter);
        if (!hasSupplier) return false;
      }
      if (!needle) return true;
      const inHeader =
        String(o.id).toLowerCase().includes(needle) ||
        (o.supplier || "").toLowerCase().includes(needle);
      const inItems = (o.items || []).some((it) => {
        const fields = [it.name, it.sku, it.supplier, it.category, it.website, it.unit]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return fields.includes(needle);
      });
      return inHeader || inItems;
    });
  }, [enrichedOrders, q, statusFilter, supplierFilter]);

  return (
    <div className="min-h-screen bg-neutral-900 text-white p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <button
          onClick={() => { play("tap"); buzz("light"); navigate("/dashboard"); }}
          className="px-3 py-1 bg-orange-500 rounded"
        >
          ← Back to Dashboard
        </button>
      </div>

      <h1 className="text-2xl font-bold mb-4">Purchase Orders</h1>

      {/* Suggestion block */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">
            Reorder Suggestions <span className="text-pink-300">({suggestions.length})</span>
          </div>
        </div>

        {suggestions.length === 0 ? (
          <div className="text-sm text-zinc-400">All items are above their limits.</div>
        ) : (
          <>
            <div className="grid gap-3 mb-3">
              {suggestions.map((s) => {
                const qty = qtyFor(s.id, s.suggestedQty);
                const line = qty * Number(s.unitCost || 0);
                return (
                  <div key={s.id} className="p-3 bg-neutral-800 rounded flex items-start gap-3">
                    {s.image ? <img src={s.image} alt={s.name} className="h-12 w-12 rounded object-cover" /> : <div className="h-12 w-12 rounded bg-neutral-700" />}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold truncate">{s.name}</div>
                        <div className="text-xs opacity-70">${Number(s.unitCost || 0).toFixed(4)} / {s.unit}</div>
                      </div>
                      <div className="text-xs opacity-70 truncate">
                        SKU: {s.sku || "—"} • Stock: {s.stock} {s.unit} • Min: {s.min || 0}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs opacity-70">Suggest:</span>
                        <input
                          type="number"
                          min="0"
                          value={qty}
                          onChange={(e) => setQtyOverrides((m) => ({ ...m, [s.id]: Number(e.target.value || 0) }))}
                          className="w-24 px-2 py-1 rounded bg-neutral-900 border border-neutral-700 text-right"
                        />
                        <span className="text-xs opacity-70">{s.unit}</span>
                        <div className="ml-auto text-xs">Subtotal: <span className="font-semibold">${line.toFixed(2)}</span></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end">
              <button
                onClick={createDraftFromSuggestions}
                disabled={!suggestions.length}
                className={`px-3 py-2 rounded ${suggestions.length ? "bg-orange-600 hover:bg-orange-700" : "bg-orange-900/40 cursor-not-allowed"}`}
                title="Create a draft PO from the suggestions above"
              >
                Create Draft from Suggestions
              </button>
            </div>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search orders & lines…"
          className="px-3 py-2 rounded bg-neutral-800 border border-neutral-700"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded bg-neutral-800 border border-neutral-700"
        >
          {["All", "Draft", "Open", "Received"].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          className="px-3 py-2 rounded bg-neutral-800 border border-neutral-700"
        >
          {suppliers.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Orders list */}
      {filteredOrders.length === 0 ? (
        <p>No matching purchase orders.</p>
      ) : (
        filteredOrders.map((o) => {
          const total = orderTotal(o);
          const editing = editOrderId === o.id;
          return (
            <div key={o.id} className="bg-neutral-800 p-4 mb-4 rounded">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-lg font-semibold">Order #{o.id}</div>
                <div className="text-sm opacity-80">{new Date(o.createdAt).toLocaleString()}</div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left opacity-70">
                      <th className="py-2 pr-3">Item</th>
                      <th className="py-2 pr-3">SKU</th>
                      <th className="py-2 pr-3">Supplier</th>
                      <th className="py-2 pr-3">Website</th>
                      <th className="py-2 pr-3 text-right">Unit</th>
                      <th className="py-2 pr-3 text-right">Unit Cost</th>
                      <th className="py-2 pr-3 text-right">Qty</th>
                      <th className="py-2 pr-0 text-right">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(o.items || []).map((it) => {
                      const key = it.id || it.materialId;
                      const qty = editing ? Number(editLines[key] ?? it.qty ?? 0) : Number(it.qty || 0);
                      const line = Number(it.unitCost || 0) * qty;
                      const url = normalizeUrl(it.website);

                      return (
                        <tr key={key}>
                          <td className="py-2 pr-3">
                            <div className="flex items-center gap-3">
                              {it.image ? <img src={it.image} alt={it.name} className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-neutral-700" />}
                              <div className="min-w-0">
                                <div className="font-medium truncate">{it.name}</div>
                                <div className="text-xs opacity-70 truncate">{it.category || "—"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-2 pr-3">{it.sku || "—"}</td>
                          <td className="py-2 pr-3">{it.supplier || "—"}</td>
                          <td className="py-2 pr-3">
                            {url ? (
                              <a className="text-amber-300 hover:underline" href={url} target="_blank" rel="noreferrer" onClick={() => play("tap")}>
                                Link
                              </a>
                            ) : "—"}
                          </td>
                          <td className="py-2 pr-3 text-right">{it.unit || "unit"}</td>
                          <td className="py-2 pr-3 text-right">{currency(Number(it.unitCost || 0))}</td>
                          <td className="py-2 pr-3 text-right">
                            {editing ? (
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) => setEditLines((m) => ({ ...m, [key]: Number(e.target.value || 0) }))}
                                onBlur={() => play("tap")}
                                className="w-20 bg-neutral-900 border border-neutral-700 rounded px-2 py-1 text-right"
                              />
                            ) : qty}
                          </td>
                          <td className="py-2 pr-0 text-right">{currency(line)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={7} className="py-3 text-right font-semibold">Total</td>
                      <td className="py-3 text-right font-semibold">{currency(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 justify-end">
                {editing ? (
                  <>
                    <button onClick={() => saveEdit(o)} className="px-3 py-2 rounded bg-emerald-500 text-black hover:bg-emerald-400 font-semibold">Save</button>
                    <button onClick={() => { setEditOrderId(null); setEditLines({}); play("tap"); }} className="px-3 py-2 rounded bg-neutral-700 hover:bg-neutral-600">Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => beginEdit(o)} className="px-3 py-2 rounded bg-neutral-700 hover:bg-neutral-600">Edit</button>
                    {o.status !== "Received" && (
                      <button onClick={() => markReceived(o)} className="px-3 py-2 rounded bg-amber-500 text-black hover:bg-amber-400 font-semibold">Mark Received</button>
                    )}
                    <button onClick={() => deleteOrder(o.id)} className="px-3 py-2 rounded bg-red-600 hover:bg-red-500">Delete</button>
                  </>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Create PO Modal (from widget draft or suggestions) */}
      {showCreate && (
        <POCreateModal
          lowItems={draftItems}
          onClose={() => { setShowCreate(false); setDraftItems([]); j.del(LS_DRAFT); }}
          onSaved={() => { setShowCreate(false); setDraftItems([]); j.del(LS_DRAFT); }}
        />
      )}
    </div>
  );
}
